import React, { useState, useEffect } from "react";
import Button from "../Button/Button";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModalNew";
import EditableGrid from "../EditableGrid/EditableGrid";
import Layout from "../Layout/Layout";
import PageHeader from "../PageHeader/PageHeader";
import { Container, Row, Col, FormGroup, Input } from "reactstrap";
import { OutlineBtn, GridWrapper } from "../../styles/commons";
import { Prompt } from "react-router-dom";
import { gridSettings } from "./gridSettings";
import { useGetScenarios } from "./useGetScenarios";
import { useAddScenario } from "./useAddScenario";
import { useDeleteScenario } from "./useDeleteScenario";
import { usePrevious } from "../../hooks/usePrevious";
import { useSuccessToast } from "../../hooks/useSuccessToast";
import { useUpdateAllScenarios } from "./useUpdateAllScenarios";

const AdminScenarios = () => {
  const { setSuccessMsg, successToast } = useSuccessToast();
  const [name, setName] = useState("");
  const [deleteModal, setDeleteModal] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [form, setForm] = useState([]);
  const prevForm = usePrevious(form);

  const [{ data: scenarios }, refetch] = useGetScenarios();

  useEffect(() => {
    if (scenarios.length > 0) {
      setForm(scenarios);
    }
  }, [scenarios]);

  const unsavedChanges = prevForm && prevForm.length > 0 && prevForm !== form;
  window.onbeforeunload = () => (unsavedChanges ? true : undefined);

  const { rows, cols } = gridSettings(form, setForm, setDeleteModal);

  const setYearObj = (i) => ({
    year: i,
    grossSales: "",
    cogs: "",
    ga: "",
    itda: "",
    totalDebt: "",
    otherIncome: "",
  });

  function addYear() {
    const newForm = [...form];
    const arr = newForm.map((item) => {
      const obj = { ...item };
      obj.years.push(setYearObj(obj.years.length + 1));
      return obj;
    });
    setForm(arr);
    setSuccessMsg("Year added.");
  }

  function removeYear() {
    const newForm = [...form];
    const arr = newForm.map((item) => {
      const obj = { ...item };
      obj.years.pop();
      return obj;
    });
    setForm(arr);
    setSuccessMsg("Year removed.");
  }

  const newScenarioYears = form[0]
    ? Array.apply(null, Array(form[0].years.length)).map((item, i) =>
        setYearObj(i + 1)
      )
    : [];

  const [, addScenario] = useAddScenario(
    { name, years: newScenarioYears, lastUpdated: new Date() },
    () => {
      refetch();
      setName("");
      setAddModal(false);
    }
  );

  const [, deleteScenario] = useDeleteScenario(
    deleteModal ? deleteModal.forecastId : null,
    () => {
      refetch();
      setDeleteModal(null);
    }
  );

  const [, updateScenarios] = useUpdateAllScenarios(form, () => {
    refetch();
    setSuccessMsg("Scenarios updated.");
  });



  return (
    <Layout product="stratPlan">
      <Prompt
        when={unsavedChanges}
        message="You have unsaved changes, are you sure you want to leave? Hit OK if you want to leave the page without saving changes."
      />
      <PageHeader title="Scenarios">
        <div style={{ float: "right" }}>
          <OutlineBtn style={{ marginRight: 10 }} onClick={updateScenarios}>
            Save
          </OutlineBtn>
          <Button click={() => setAddModal(true)}>Add Scenario</Button>
        </div>
      </PageHeader>

      <div style={{ padding: 20 }}>
        <Container fluid>
          <Row>
            <Col>
              <Button small color="#1cc7c8" click={addYear} marginRight="10px">
                Add Year
              </Button>
              <Button small color="#9699b4" click={removeYear}>
                Remove Year
              </Button>
            </Col>
          </Row>
          <div style={{ height: 10 }} />
          <Row>
            <Col>
              <GridWrapper>
                <EditableGrid cols={cols} rows={rows} groupedRows={true} />
              </GridWrapper>
            </Col>
          </Row>
        </Container>
      </div>

      {deleteModal && (
        <ConfirmationModal
          close={() => setDeleteModal(null)}
          buttons={[
            {
              name: "Delete",
              marginRight: "10px",
              click: () => deleteScenario(deleteModal.id),
              color: "#F82228",
            },
            {
              name: "Cancel",
              color: "#ecedf4",
              textColor: "#333",
              click: () => setDeleteModal(null),
            },
          ]}
        >
          <p>
            Are you sure you want to delete the <b>{deleteModal.name}</b>{" "}
            scenario?
            <br />
            This cannot be undone.
          </p>
        </ConfirmationModal>
      )}

      {addModal && (
        <ConfirmationModal
          close={() => setAddModal(false)}
          buttons={[
            {
              name: "Add Scenario",
              marginRight: "10px",
              click: addScenario,
              disabled: !name,
            },
            {
              name: "Cancel",
              color: "#ecedf4",
              textColor: "#333",
              click: () => setAddModal(false),
            },
          ]}
        >
          <p>Scenario Name</p>
          <FormGroup>
            <Input
              bsSize="sm"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormGroup>
        </ConfirmationModal>
      )}
      {successToast}
    </Layout>
  );
};

export default AdminScenarios;
